import { useNavigate } from "@tanstack/react-router";
import {
  CheckSquare2Icon,
  CircleIcon,
  FileCheckIcon,
  FileUpIcon,
  FlaskConicalIcon,
} from "lucide-react";

import { Button, type ButtonProps } from "@/components/ui/button";
import { useAuth } from "@/features/auth/hooks";
import { useNavLocationState } from "@/utils/useNavStack";
import { useAccessionCommands } from "./AccessionCommands";
import { useAccession } from "./AccessionProvider";
import { useSetDialogState } from "./detail/state";

export function ActionButton(props: ButtonProps) {
  const context = useAccession();
  if (context.testOrders.length === 0) return null;

  const disableTestRun = context.testOrders.every(
    (to) => to.testSpec.disableTestRun,
  );

  const disableReport = context.testOrders.every(
    (to) => to.testSpec.disableReport,
  );

  switch (context.accession.status) {
    case "new":
      return <ReceiveButton {...props} />;
    case "received":
      return disableTestRun ? (
        <EnterResultsButton {...props} />
      ) : (
        <CreateTestRunButton {...props} />
      );
    case "in_progress":
      return <EnterResultsButton {...props} />;
    case "completed":
      return disableReport ? null : <GenerateReportButton {...props} />;
    case "cancelled":
      return <RestoreButton {...props} />;
    default:
      return null;
  }
}

function ReceiveButton(props: ButtonProps) {
  const { receive } = useAccessionCommands();
  return (
    <Button onPress={() => receive()} {...props}>
      <CircleIcon />
      Receive
    </Button>
  );
}

function CreateTestRunButton(props: ButtonProps) {
  const { createTestRun } = useAccessionCommands();
  return (
    <Button onPress={() => createTestRun()} {...props}>
      <FlaskConicalIcon />
      Create test run
    </Button>
  );
}

function EnterResultsButton(props: ButtonProps) {
  const { enterResults } = useAccessionCommands();
  const { user } = useAuth();
  if (!user?.canHandleResults()) {
    return null;
  }
  return (
    <Button onPress={() => enterResults()} {...props}>
      <CheckSquare2Icon />
      Enter results
    </Button>
  );
}

function GenerateReportButton(props: ButtonProps) {
  const { accession } = useAccession();
  const navigate = useNavigate();
  const { href, label } = useNavLocationState();
  const { user } = useAuth();
  if (!user?.canHandleResults()) {
    return null;
  }
  return (
    <Button
      onPress={() => {
        navigate({
          to: "/accessions/$id/report",
          params: { id: accession.id },
          state: { href, label },
        });
      }}
      {...props}
    >
      <FileCheckIcon />
      Generate report
    </Button>
  );
}

function RestoreButton(props: ButtonProps) {
  const setShowRestore = useSetDialogState("restore");
  return (
    <Button onPress={() => setShowRestore(true)} {...props}>
      <FileUpIcon />
      Restore
    </Button>
  );
}
